import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiBaseService } from './api-base.service';
import { AdminOrderDetails, AdminOrderListResponse, AdminOrderSearchResult, AdminPodData, AdminImageDiagnostics, } from './_dtos/order.dto';

@Injectable({
  providedIn: 'root',
})
export class AdminOrdersApiService extends ApiBaseService {
  private readonly baseUrl = 'admin/orders';

  /**
   * Get paginated orders list with filters
   */
  getOrders(params: {
    page?: number;
    per_page?: number;
    sort?: string;
    'filter[order_number]'?: string;
    'filter[status]'?: string;
    'filter[user_email]'?: string;
  }): Observable<AdminOrderListResponse> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    return this.get<AdminOrderListResponse>(this.baseUrl, httpParams);
  }

  /**
   * Search orders by order number (autocomplete)
   */
  searchOrders(query: string): Observable<{ data: AdminOrderSearchResult[] }> {
    const httpParams = new HttpParams().set('query', query);
    return this.get<{ data: AdminOrderSearchResult[] }>(`${this.baseUrl}/search`, httpParams);
  }

  /**
   * Get order details with POD data
   */
  getOrderDetails(id: string): Observable<{ data: AdminOrderDetails }> {
    return this.get<{ data: AdminOrderDetails }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Get POD provider details for specific order item
   */
  getPodDetails(
    orderId: string,
    itemId: string
  ): Observable<{ data: AdminPodData | null; error?: string }> {
    return this.get<{ data: AdminPodData | null; error?: string }>(
      `${this.baseUrl}/${orderId}/items/${itemId}/pod-details`
    );
  }

  /**
   * Get mockup URL for specific order item (from POD provider)
   */
  getMockup(orderId: string, itemId: string, force = false): Observable<{ mockup_url: string | null; error?: string }> {
    const params = force ? '?force=1' : '';
    return this.get<{ mockup_url: string | null; error?: string }>(
      `${this.baseUrl}/${orderId}/items/${itemId}/mockup${params}`
    );
  }

  /**
   * Retry POD submission for an order (when upscale/submission failed)
   */
  retryPodSubmission(orderId: string): Observable<{ success: boolean; message: string; results?: any }> {
    return this.post<{ success: boolean; message: string; results?: any }>(
      {},
      `${this.baseUrl}/${orderId}/retry-pod`
    );
  }

  /**
   * Delete an order with optional cascade of related data
   */
  deleteOrder(orderId: string, cascadeRelated: boolean = false): Observable<{ message: string }> {
    const cascade = cascadeRelated ? '?cascade_related=1' : '';
    return this.delete<{ message: string }>(`${this.baseUrl}/${orderId}${cascade}`);
  }

  /**
   * Get image processing diagnostics for a specific order item
   */
  getImageDiagnostics(orderId: string, itemId: string): Observable<{ data: AdminImageDiagnostics | null; error?: string }> {
    return this.get<{ data: AdminImageDiagnostics | null; error?: string }>(
      `${this.baseUrl}/${orderId}/items/${itemId}/image-diagnostics`
    );
  }

  /**
   * Get orders pending admin review
   */
  getPendingReviewOrders(params: {
    page?: number;
    per_page?: number;
  } = {}): Observable<AdminOrderListResponse> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    return this.get<AdminOrderListResponse>(`${this.baseUrl}/pending-review`, httpParams);
  }

  /**
   * Accept an order and confirm with POD provider
   */
  acceptOrder(orderId: string): Observable<{ success: boolean; message: string; next_order_id: string | null }> {
    return this.post<{ success: boolean; message: string; next_order_id: string | null }>(
      {},
      `${this.baseUrl}/${orderId}/accept`
    );
  }

  /**
   * Get count of orders pending admin review
   */
  getPendingReviewCount(): Observable<{ count: number }> {
    return this.get<{ count: number }>(`${this.baseUrl}/pending-review/count`);
  }
}
